import Label from '@components/forms/Label'
import Button from '@components/shared/Button'
import InlineNotification from '@components/shared/InlineNotification'
import Modal from '@components/shared/Modal'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { useSolPrice } from 'hooks/useSolPrice'
import { useState } from 'react'
import { NumericFormat } from 'react-number-format'
import { ModalProps } from 'types/modal'
import {
  DEFAULT_TRADE_VALUE_FILTER,
  getTradeSizeEmoji,
  TRADE_VALUE_FILTER_KEY,
} from 'utils/constants'
import { NumberRangeFilter } from 'utils/formatting'
import { numberCompacter } from 'utils/numbers'
import { useTranslation } from 'next-i18next'

const DEFAULT_VALUE_FILTERS = [
  [0, 500],
  [500, 2000],
  [2000, 10000],
  [10000, Infinity],
]

const INPUT_CLASSES =
  'w-full rounded-md bg-th-input-bkg px-3 py-2 text-base text-left text-th-fgd-1 focus:outline-none focus-visible:border-th-fgd-4 md:hover:border-th-input-border-hover md:hover:focus-visible:border-th-fgd-4'

const ValueFilterFormModal = ({ isOpen, onClose }: ModalProps) => {
  const { t } = useTranslation('trade')
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <h2 className="mb-4 text-center">{t('filter.title.value')}</h2>
      <ValueFilterForm onSave={onClose} />
    </Modal>
  )
}

export default ValueFilterFormModal

type FormErrors = { min: string; max: string }
const DEFAULT_ERRORS = { min: '', max: '' }

export const ValueFilterForm = ({ onSave }: { onSave: () => void }) => {
  const { t } = useTranslation('trade')
  const [valueFilter, setValueFilter] = useLocalStorage(TRADE_VALUE_FILTER_KEY)
  const [customFilterValues, setCustomFilterValues] =
    useState<NumberRangeFilter>(
      valueFilter.USDC.min ? valueFilter : DEFAULT_TRADE_VALUE_FILTER,
    )
  const [formErrors, setFormErrors] = useState<FormErrors>(DEFAULT_ERRORS)
  const { solPrice } = useSolPrice()

  const isFormValid = (form: NumberRangeFilter) => {
    const invalidFields: FormErrors = { min: '', max: '' }
    const formToValidate = form.USDC

    setFormErrors(DEFAULT_ERRORS)

    if (
      formToValidate.max &&
      parseFloat(formToValidate.min) >= parseFloat(formToValidate.max)
    ) {
      invalidFields.min = 'Min value must be less than max'
    }

    const hasErrors = !!invalidFields.min || !!invalidFields.max
    if (hasErrors) {
      setFormErrors(invalidFields)
    }

    return hasErrors ? invalidFields : {}
  }

  const handleFilterChange = (value: NumberRangeFilter) => {
    setFormErrors(DEFAULT_ERRORS)
    setCustomFilterValues(value)
  }

  const handleApplyFilter = () => {
    const invalidFields = isFormValid(customFilterValues)
    if (Object.keys(invalidFields).length) {
      return
    }
    const min = customFilterValues.USDC?.min
      ? (parseFloat(customFilterValues.USDC.min) / solPrice).toString()
      : ''
    const max = customFilterValues.USDC?.max
      ? (parseFloat(customFilterValues.USDC.max) / solPrice).toString()
      : ''

    const newFilterValues = {
      ...customFilterValues,
      SOL: {
        ...customFilterValues.SOL,
        min,
        max,
      },
    }

    setValueFilter(newFilterValues)
    onSave()
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-2 pb-4">
        {DEFAULT_VALUE_FILTERS.map((filter) => (
          <button
            className={`col-span-1 rounded-md px-3 py-2 font-mono focus:outline-none ${
              customFilterValues.USDC.min === filter.toString()
                ? 'bg-th-active text-th-fgd-1'
                : 'bg-th-bkg-3 text-th-fgd-2'
            }`}
            key={filter[1]}
            onClick={() =>
              handleFilterChange({
                SOL: { min: '', max: '' },
                USDC: {
                  min: filter[0].toString(),
                  max: filter[1].toString(),
                },
              })
            }
          >
            <span className="mr-2 text-lg">{getTradeSizeEmoji(filter[1])}</span>
            <span>{`$${numberCompacter.format(filter[0])}${
              filter[1] !== Infinity
                ? `–$${numberCompacter.format(filter[1])}`
                : '+'
            }`}</span>
          </button>
        ))}
      </div>
      <div className="flex space-x-2">
        <div className="w-full">
          <Label text={t('filter.value.min')} />
          <NumericFormat
            name="min"
            id="min"
            inputMode="numeric"
            lang="en"
            allowedDecimalSeparators={['.', ',']}
            thousandSeparator=","
            allowNegative={false}
            className={INPUT_CLASSES}
            placeholder={t('filter.value.min')}
            value={customFilterValues.USDC?.min}
            onValueChange={(values) => {
              setCustomFilterValues({
                ...customFilterValues,
                USDC: { min: values.value, max: customFilterValues.USDC.max },
              })
              setFormErrors(DEFAULT_ERRORS)
            }}
          />
        </div>
        <div className="w-full">
          <Label text={t('filter.value.max')} />
          <NumericFormat
            name="max"
            id="max"
            inputMode="numeric"
            lang="en"
            allowedDecimalSeparators={['.', ',']}
            thousandSeparator=","
            allowNegative={false}
            className={INPUT_CLASSES}
            placeholder={t('filter.value.max')}
            value={customFilterValues.USDC?.max}
            onValueChange={(values) => {
              setCustomFilterValues({
                ...customFilterValues,
                USDC: { min: customFilterValues.USDC.min, max: values.value },
              })
              setFormErrors(DEFAULT_ERRORS)
            }}
          />
        </div>
      </div>
      {formErrors.min.length ? (
        <div className="mt-2">
          <InlineNotification
            type="error"
            hideBorder
            hidePadding
            desc={formErrors.min}
          />
        </div>
      ) : null}
      <div className="flex justify-end space-x-2 pt-4">
        <Button
          disabled={!customFilterValues.USDC.min}
          onClick={() => handleApplyFilter()}
        >
          {t('filter.value.apply')}
        </Button>
        <Button
          onClick={() => {
            setValueFilter(DEFAULT_TRADE_VALUE_FILTER)
            onSave()
          }}
          secondary
        >
          {t('filter.value.clear')}
        </Button>
      </div>
    </>
  )
}
